import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'expo-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Text, YStack } from 'tamagui'
import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import type { RequestCard } from '../../../lib/types'
import { Button, DateField, FormTextArea, Link } from '../../../ui'
import { useMe } from '../../users/useUsers'
import { errorText } from '../errorText'
import { canEditTerminationDraft, type TerminationInput } from '../termination-types'
import { EmploymentField, employmentLabel, useEmploymentSelection } from './EmploymentField'
import { Field } from './shared'

/** This form has no employee/proxy picker: identity belongs to the authenticated user. */
export function TerminationForm({ card, onDone, operationLock, busy, onBusyChange }: { card?: RequestCard; onDone: () => void; operationLock?: { current: boolean }; busy?: boolean; onBusyChange?: (busy: boolean) => void }) {
  const { data: me } = useMe()
  if (card) {
    if (!canEditTerminationDraft(card, me?.id) || !card.employment?.id) return null
    return <ScopedTerminationForm key={card.id} card={card} employmentId={card.employment.id} companyId={card.employment.companyId} companyName={card.employment.companyName} saving={operationLock} busy={busy} onBusyChange={onBusyChange} onDone={onDone} />
  }
  return <NewTerminationForm onDone={onDone} />
}
function NewTerminationForm({ onDone }: { onDone: () => void }) {
  const { t } = useApp()
  const router = useRouter()
  const employment = useEmploymentSelection()
  const saving = useRef(false)
  const [locked, setLocked] = useState(false)
  const job = employment.jobs.find(j => j.id === employment.employmentId)
  return <YStack gap={12}>
    {/* Руководители и HR открывали эту плитку, чтобы уволить подчинённого (тест, 02.11.2026), — выбора сотрудника здесь нет и не будет. */}
    <Text color="$muted">{t({ ru: 'Увольняете подчинённого? Это другая заявка:', en: 'Dismissing your report? That is a separate request:', az: 'Tabeliyinizdəki işçini işdən çıxarırsınız? Bunun üçün ayrıca müraciət var:' })} <Link onPress={() => router.push('/processes/new/fire_employer')}>{t({ ru: 'Увольнение сотрудника', en: 'Employee dismissal', az: 'İşçinin işdən çıxarılması' })}</Link></Text>
    {locked && job ? <Text>{employmentLabel(job)}</Text> : <EmploymentField selection={{ ...employment, select: id => { if (!saving.current && !locked) employment.select(id) } }} />}
    {job ? <ScopedTerminationForm key={job.id} employmentId={job.id} companyId={job.companyId} companyName={job.companyName} saveAllowed={employment.ready} saving={saving} onCreated={() => setLocked(true)} onDone={onDone} /> : !employment.loading && !employment.error && <Text color="$muted">{t({ ru: 'Для черновика увольнения выберите своё действующее место работы.', en: 'Select your active workplace to prepare a termination draft.' })}</Text>}
  </YStack>
}
function ScopedTerminationForm({ card, employmentId, companyId, companyName, saving: sharedSaving, busy = false, saveAllowed = true, onBusyChange, onCreated, onDone }: {
  card?: RequestCard; employmentId: string; companyId?: string; companyName?: string
  saving?: { current: boolean }; busy?: boolean; saveAllowed?: boolean; onBusyChange?: (busy: boolean) => void; onCreated?: () => void; onDone: () => void
}) {
  const { t } = useApp()
  const qc = useQueryClient()
  const localSaving = useRef(false)
  const saving = sharedSaving ?? localSaving
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const payload = card?.payload as unknown as TerminationInput | undefined
  const [f, setF] = useState<Required<TerminationInput>>({ terminationDate: payload?.terminationDate ?? '', terminationReasonId: payload?.terminationReasonId ?? '', reason: payload?.reason ?? '' })
  const [requestId, setRequestId] = useState(card?.id)
  const refs = useQuery({ queryKey: ['termination-references', employmentId], queryFn: () => api.terminationReferences(employmentId), retry: false })
  const wrongScope = !!refs.data && !!companyId && refs.data.companyId !== companyId
  // Основание не выбирает сотрудник: ст. 69.1 подставит сервер при подаче (ответ аналитика 07.10.2026).
  const basis = wrongScope ? null : refs.data?.basis ?? null
  const earliest = refs.data?.earliestDate
  const patch = (key: keyof TerminationInput, value: string) => { if (!saving.current) setF(prev => ({ ...prev, [key]: value })) }
  const save = useMutation({
    mutationFn: (input: TerminationInput) => requestId ? api.saveTermination(requestId, input) : api.createTerminationDraft({ employmentId, ...input }),
    onSuccess: req => {
      qc.invalidateQueries({ queryKey: ['edo-request', req.id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      if (!mounted.current) return
      setRequestId(req.id)
      if (!requestId) { onCreated?.(); onDone() }
    },
    onSettled: () => { saving.current = false; onBusyChange?.(false) },
  })
  const submitSave = () => {
    if (saving.current || wrongScope || !saveAllowed) return
    saving.current = true
    onBusyChange?.(true)
    save.mutate({ terminationDate: f.terminationDate, reason: f.reason })
  }
  // Подача: сохранить текущие поля и отправить на согласование одним действием.
  const submit = useMutation({
    mutationFn: async () => {
      const input: TerminationInput = { terminationDate: f.terminationDate, reason: f.reason }
      const req = requestId ? await api.saveTermination(requestId, input) : await api.createTerminationDraft({ employmentId, ...input })
      if (mounted.current) setRequestId(req.id)
      return api.submitTermination(req.id)
    },
    onSuccess: req => {
      qc.invalidateQueries({ queryKey: ['edo-request', req.id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      onCreated?.()
      onDone()
    },
    onSettled: () => { saving.current = false; onBusyChange?.(false) },
  })
  const tooEarly = !!earliest && !!f.terminationDate && f.terminationDate < earliest
  const canSubmit = !!refs.data?.canSubmit && !wrongScope && saveAllowed && !!f.terminationDate && !tooEarly && !!f.reason.trim()
  const doSubmit = () => {
    if (saving.current || !canSubmit) return
    saving.current = true
    onBusyChange?.(true)
    submit.mutate()
  }
  const pending = save.isPending || submit.isPending || busy
  return <YStack gap={12}>
    <Text fontWeight="700">{t({ ru: 'Увольнение по инициативе сотрудника', en: 'Employee-initiated termination' })}</Text>
    <Text color="$muted">{t({ ru: 'Заявка пройдёт согласование: руководитель → руководитель подразделения → HR → Exit-интервью → CEO. После решения CEO HR оформит приказ в 1С.', en: 'Approval route: manager → head of unit → HR → exit interview → CEO. After the CEO decision HR issues the order in 1C.', az: 'Razılaşdırma: rəhbər → bölmə rəhbəri → HR → Exit-müsahibə → CEO. CEO qərarından sonra HR 1C-də əmr hazırlayır.' })}</Text>
    <Text>{t({ ru: 'Сотрудник', en: 'Employee' })}: {wrongScope ? '—' : refs.data?.employeeName ?? card?.initiator.fullName ?? '—'}</Text>
    <Text>{t({ ru: 'Компания', en: 'Company' })}: {companyName ?? '—'}</Text>
    <Field label={t({ ru: 'Дата увольнения', en: 'Termination date' })}><DateField value={f.terminationDate} min={earliest} disabled={pending} onChange={v => patch('terminationDate', v)} /></Field>
    {!!earliest && <Text fontSize={12} color={tooEarly ? '$red10' : '$muted'}>{t({ ru: `По ст. 69.1 ТК АР — не раньше чем через месяц после подачи: не ранее ${earliest}`, en: `Art. 69.1: at least one month after submission — not before ${earliest}`, az: `Maddə 69.1: təqdimatdan ən azı bir ay sonra — ${earliest} tarixindən tez olmayaraq` })}</Text>}
    {!!f.terminationDate && <Button disabled={pending} onPress={() => patch('terminationDate', '')}>{t({ ru: 'Очистить дату', en: 'Clear date' })}</Button>}
    <Field label={t({ ru: 'Причина увольнения (вашими словами)', en: 'Reason for leaving (in your words)' })}><FormTextArea value={f.reason} editable={!pending} onChangeText={v => patch('reason', v)} /></Field>
    <Text>{t({ ru: 'Основание', en: 'Legal basis', az: 'Əsas' })}: {basis ? (basis.text ? `${basis.name} — ${basis.text}` : basis.name) : refs.isPending ? '…' : '—'}</Text>
    {wrongScope && <Text color="$red10">{t({ ru: 'Справочник другой компании. Закройте и откройте форму заново.', en: 'References belong to another company. Reopen the form.' })}</Text>}
    {!wrongScope && refs.data?.blockers.map((b, i) => <Text key={i} color="$red10">{b}</Text>)}
    {refs.error instanceof Error && <Text color="$red10">{t(errorText(refs.error))}</Text>}
    <Button disabled={pending || wrongScope || !saveAllowed} onPress={submitSave}>{t({ ru: 'Сохранить черновик', en: 'Save draft' })}</Button>
    <Button variant="primary" disabled={pending || !canSubmit} onPress={doSubmit}>{t({ ru: 'Отправить на согласование', en: 'Submit for approval', az: 'Razılaşdırmaya göndər' })}</Button>
    {save.isSuccess && <Text>{t({ ru: 'Черновик сохранён', en: 'Draft saved' })}</Text>}
    {save.error instanceof Error && <Text color="$red10">{t(errorText(save.error))}</Text>}
    {submit.error instanceof Error && <Text color="$red10">{t(errorText(submit.error))}</Text>}
  </YStack>
}
